import React from 'react';
import { forEach, includes, isEmpty, map } from 'lodash';
import {
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import CustomChip from 'components/CustomChip/CustomChip';
import LinkToSectionScroll from 'components/LinkToSectionScroll/LinkToSectionScroll';
import { chipColorMap } from 'utils/colorMappers';
import { formatDataValue, formatShippingPriority } from 'utils/formatFns';
import { getSharedLabelsAndValues } from '../sharedData/sharedDataConfig';

const getCompleteChip = (isPrint, chipId, searchContextValues) => (
  <CustomChip
    label="COMPLETE"
    color="success.10"
    backgroundColor="success.100"
    iconPosition="right"
    isPrint={isPrint}
    chipId={chipId}
    icon={
      <CheckCircleIcon
        sx={{
          width: isPrint ? '0.625rem' : '1rem',
          height: isPrint ? '0.625rem' : '1rem',
        }}
      />
    }
    searchContextValues={searchContextValues}
  />
);

const getPendingChip = (isPrint, chipId, searchContextValues) => (
  <CustomChip
    label="PENDING"
    color={chipColorMap?.allocationUnassignedText}
    backgroundColor={chipColorMap?.etaStatusAtRiskBg}
    iconPosition="right"
    isPrint={isPrint}
    chipId={chipId}
    icon={
      <PendingIcon
        sx={{
          width: isPrint ? '0.625rem' : '1rem',
          height: isPrint ? '0.625rem' : '1rem',
        }}
      />
    }
    searchContextValues={searchContextValues}
  />
);

const getStatusChip = ({ eventStatus, isPrint, chipId, searchContextValues }) => {
  switch (eventStatus) {
    case 'Complete':
      return getCompleteChip(isPrint, chipId, searchContextValues);
    case 'Pending':
      return getPendingChip(isPrint, chipId, searchContextValues);
    default:
      return '';
  }
};

const isPending = ({ eventStatus }) => {
  switch (eventStatus) {
    case 'Pending':
      return true;
    default:
      return false;
  }
};

const getTitleChip = ({ departureData, arrivalData, isPrint, searchContextValues }) => {
  const label = departureData?.railCarSighting
    ? `last sighting ${departureData?.railCarSighting}`
    : '';
  const isChipShow =
    departureData?.status === 'Complete' && arrivalData?.status === 'Pending' && !!label;

  return isChipShow ? (
    <CustomChip
      label={label}
      color={chipColorMap?.qualityConfActiveText}
      backgroundColor={chipColorMap?.qualityConfActiveBackground}
      iconPosition="left"
      isPrint={isPrint}
      icon={
        <VisibilityIcon
          sx={{
            width: isPrint ? '0.625rem' : '1rem',
            height: isPrint ? '0.625rem' : '1rem',
          }}
        />
      }
      searchContextValues={searchContextValues}
    />
  ) : null;
};

const getDateValue = (data) => {
  if (data?.status === 'Pending') {
    return formatDataValue({
      value:
        data?.estimatedOccurrenceWindowStartFmt && data?.estimatedOccurrenceWindowEndFmt
          ? `${data?.estimatedOccurrenceWindowStartFmt} - ${data?.estimatedOccurrenceWindowEndFmt}`
          : data?.estimatedOccurrenceDateFmt || 'Not Available',
    });
  }
  if (data?.status === 'Complete' && data?.actualOccurrenceDate) {
    return formatDataValue({
      value: data?.actualOccurrenceDateFmt,
      fallbackString: 'Not Available',
    });
  }
  return 'Not Available';
};

export const getVesselValue = ({ vehicle }) => {
  if (
    vehicle?.manufacturing?.plantInformation?.napCbuCode === 'Import' &&
    !!vehicle?.vehicleTracking?.vesselTransport?.etdActualVesselFmt
  ) {
    return `${formatDataValue({
      value: vehicle?.vehicleTracking?.vesselTransport?.shipmentYear,
      fallbackString: 'Not Available',
    })} | ${formatDataValue({
      value: vehicle?.vehicleTracking?.vesselTransport?.legacyVesselNumber,
      fallbackString: 'Not Available',
    })}`;
  }
  if (
    vehicle?.manufacturing?.plantInformation?.napCbuCode === 'Domestic' &&
    includes(['GJ'], vehicle?.logistics?.currentShipmentAssignmentOrigin) &&
    includes(['LC', 'VC'], vehicle?.logistics?.currentVDC) &&
    !!vehicle?.vehicleTracking?.vesselTransport?.etdActualVesselFmt
  ) {
    return vehicle?.vehicleTracking?.vesselTransport?.vesselName;
  }
  return 'Not Available';
};

const getActualDate = ({ data }) => data?.actualOccurrenceDateFmt || 'Not Available';

const getEstimatedDate = ({ data }) => {
  if (data?.estimatedOccurrenceWindowStartFmt && data?.estimatedOccurrenceWindowEndFmt) {
    return `${data?.estimatedOccurrenceWindowStartFmt} - ${data?.estimatedOccurrenceWindowEndFmt}`;
  }
  if (data?.estimatedOccurrenceDateFmt) {
    return data?.estimatedOccurrenceDateFmt;
  }
  return 'Not Available';
};

const railHeaderNameMapper = (name) => {
  switch (name) {
    case 'Rail Depart':
      return 'Departure';
    case 'Rail Arrive':
      return 'Arrival';
    case 'Rail Interchange Arrival':
      return 'Interchange Arrival';
    case 'Rail Interchange Depart':
      return 'Interchange Depart';
  }
  return name;
};

export const railInterchangeMapper = ({
  departureData,
  arrivalData,
  skipDetailsInPrint,
  isPrint,
  searchContextValues,
}) => {
  const railData = {
    title: 'Rail Interchange',
    titleChip: null,
    header: [
      {
        label: railHeaderNameMapper(departureData?.name),
        value: getStatusChip({
          eventStatus: departureData?.status,
          isPrint,
          chipId: 'rail-inter-dep',
          searchContextValues,
        }),
        isComponent: true,
        isPending: isPending({ eventStatus: departureData?.status }),
      },
      {
        label: railHeaderNameMapper(arrivalData?.name),
        value: getStatusChip({
          eventStatus: arrivalData?.status,
          isPrint,
          chipId: 'rail-inter-arr',
          searchContextValues,
        }),
        isComponent: true,
        isPending: isPending({ eventStatus: arrivalData?.status }),
      },
    ],
    attributes: [
      {
        label: 'Date',
        value: getDateValue(departureData),
      },
      {
        label: 'Location',
        value: formatDataValue({
          value: departureData?.departureLocation,
          fallbackString: 'Not Available',
        }),
      },
      {
        label: 'Carrier',
        value: formatDataValue({
          value: departureData?.carrierName,
          fallbackString: 'Not Available',
        }),
      },
      {
        label: 'Railcar Number',
        value: formatDataValue({
          value: departureData?.railCarNumber,
          fallbackString: 'Not Available',
        }),
      },
      { label: 'Date', value: getDateValue(arrivalData) },
      {
        label: 'Location',
        value: formatDataValue({
          value: arrivalData?.arrivalLocation,
          fallbackString: 'Not Available',
        }),
      },
      {
        label: 'Carrier',
        value: formatDataValue({
          value: arrivalData?.carrierName,
          fallbackString: 'Not Available',
        }),
      },
      {
        label: 'Railcar Number',
        value: formatDataValue({
          value: departureData?.railCarNumber,
          fallbackString: 'Not Available',
        }),
      },
    ],
  };
  if (!skipDetailsInPrint) {
    railData.details = {
      departure: [
        {
          label: 'Actual',
          value: getActualDate({ data: departureData }),
        },
        {
          label: 'Estimate',
          value: getEstimatedDate({ data: departureData }),
        },
      ],
      arrival: [
        {
          label: 'Actual',
          value: getActualDate({ data: arrivalData }),
        },
        {
          label: 'Estimate',
          value: getEstimatedDate({ data: arrivalData }),
        },
      ],
      details: [
        {
          label: 'Carrier',
          value: formatDataValue({
            value: departureData?.carrierName,
            fallbackString: 'Not Available',
          }),
        },
        {
          label: 'Railcar Number',
          value: formatDataValue({
            value: departureData?.railCarNumber,
            fallbackString: 'Not Available',
          }),
        },
        {
          label: 'Railcar Position',
          value: formatDataValue({
            value: departureData?.railCarPosition,
            fallbackString: 'Not Available',
          }),
        },
        {
          label: 'Railcar Equip. Type',
          value: formatDataValue({
            value: departureData?.railCarEquipmentType,
            fallbackString: 'Not Available',
          }),
        },
        {
          label: 'BOL Number',
          value: formatDataValue({
            value: departureData?.billOfLading,
            fallbackString: 'Not Available',
          }),
        },
        {
          label: 'Shipment ID',
          value: formatDataValue({
            value: departureData?.shipmentId,
            fallbackString: 'Not Available',
          }),
        },
        {
          label: 'Leg Origin - Leg Destination',
          value: `${formatDataValue({
            value: departureData?.departureLocation,
            fallbackString: 'Not Available',
          })} - ${formatDataValue({
            value: arrivalData?.arrivalLocation,
            fallbackString: 'Not Available',
          })}`,
        },
      ],
    };
  }
  return railData;
};

const getDeliveryDepartureDate = (departureData) => {
  if (
    departureData?.status === 'Pending' &&
    departureData?.estimatedOccurrenceWindowStartFmt &&
    departureData?.estimatedOccurrenceWindowEndFmt
  ) {
    return `${departureData?.estimatedOccurrenceWindowStartFmt} - ${departureData?.estimatedOccurrenceWindowEndFmt}`;
  }
  if (departureData?.actualOccurrenceDateFmt || departureData?.status === 'Complete') {
    return formatDataValue({
      value: departureData?.actualOccurrenceDateFmt,
      fallbackString: 'Not Available',
    });
  }
  return 'Not Available';
};

export const deliveryMapper = ({
  departureData,
  arrivalData,
  vehicle,
  isPrint,
  searchContextValues,
}) => ({
  title: 'Delivery',
  titleChip: null,
  header: [
    {
      label: 'On Truck for Delivery',
      value: getStatusChip({
        eventStatus: departureData?.status,
        isPrint,
        chipId: 'ont-inter-dep',
        searchContextValues,
      }),
      isComponent: true,
      isPending: isPending({ eventStatus: departureData?.status }),
    },
    {
      label: 'Arrival at Dealership',
      value: getStatusChip({
        eventStatus: arrivalData?.status,
        isPrint,
        chipId: 'ont-inter-arr',
        searchContextValues,
      }),
      isComponent: true,
      isPending: isPending({ eventStatus: arrivalData?.status }),
    },
  ],
  attributes: [
    {
      label: 'Date',
      value: getDeliveryDepartureDate(departureData),
    },
    {
      label: 'Location',
      value: formatDataValue({
        value: departureData?.departureLocation,
        fallbackString: 'Not Available',
      }),
    },
    {
      label: 'Carrier',
      value: formatDataValue({
        value: departureData?.carrierName,
        fallbackString: 'Not Available',
      }),
    },
    { label: 'Date', value: getDateValue(arrivalData) },
    {
      label: 'Location',
      value: formatDataValue({
        value: arrivalData?.arrivalLocation,
        fallbackString: 'Not Available',
      }),
    },
    {
      label: 'Dealer',
      value: `${formatDataValue({
        value: vehicle?.generalInfo?.vehicleAttrs?.shipToDealerCode,
        fallbackString: 'Not Available',
      })} | ${formatDataValue({
        value: vehicle?.generalInfo?.vehicleAttrs?.shipToDealerName,
        fallbackString: 'Not Available',
      })}`,
    },
  ],
});

export const truckMapper = ({
  departureData,
  arrivalData,
  vehicle,
  isPrint,
  searchContextValues,
}) => ({
  title: 'Truck',
  titleChip: null,
  header: [
    {
      label: 'Departure',
      value: getStatusChip({
        eventStatus: departureData?.status,
        isPrint,
        chipId: 'tru-inter-dep',
        searchContextValues,
      }),
      isComponent: true,
      isPending: isPending({ eventStatus: departureData?.status }),
    },
    {
      label: 'Arrival',
      value: getStatusChip({
        eventStatus: arrivalData?.status,
        isPrint,
        chipId: 'tru-inter-arr',
        searchContextValues,
      }),
      isComponent: true,
      isPending: isPending({ eventStatus: arrivalData?.status }),
    },
  ],
  attributes: [
    {
      label: 'Date',
      value: getDateValue(departureData),
    },
    {
      label: 'Location',
      value: formatDataValue({
        value: departureData?.departureLocation,
        fallbackString: 'Not Available',
      }),
    },
    {
      label: 'Carrier',
      value: formatDataValue({
        value: departureData?.carrierName,
        fallbackString: 'Not Available',
      }),
    },
    { label: 'Date', value: getDateValue(arrivalData) },
    {
      label: 'Location',
      value: formatDataValue({
        value: arrivalData?.arrivalLocation,
        fallbackString: 'Not Available',
      }),
    },
    {
      label: 'Carrier',
      value: formatDataValue({
        value: departureData?.carrierName,
        fallbackString: 'Not Available',
      }),
    },
  ],
  details: {
    departure: [
      {
        label: 'Actual',
        value: getActualDate({ data: departureData }),
      },
      {
        label: 'Estimate',
        value: getEstimatedDate({ data: departureData }),
      },
    ],
    arrival: [
      {
        label: 'Actual',
        value: getActualDate({ data: arrivalData }),
      },
      {
        label: 'Estimate',
        value: getEstimatedDate({ data: arrivalData }),
      },
    ],
    details: [
      {
        label: 'Carrier',
        value: formatDataValue({
          value: departureData?.carrierName,
          fallbackString: 'Not Available',
        }),
      },
      {
        label: 'Tender Date',
        value: formatDataValue({
          value: departureData?.tenderedDateFmt,
          fallbackString: 'Not Available',
        }),
      },
      {
        label: 'Load Number',
        value: formatDataValue({
          value: vehicle?.vehicleTracking?.truckTransport?.loadNumber,
          fallbackString: 'Not Available',
        }),
      },
      {
        label: 'Shipment ID',
        value: formatDataValue({
          value: departureData?.shipmentId,
          fallbackString: 'Not Available',
        }),
      },
      {
        label: 'Leg Origin - Leg Destination',
        value: `${formatDataValue({
          value: departureData?.origin,
          fallbackString: 'Not Available',
        })} - ${formatDataValue({
          value: arrivalData?.arrivalLocation,
          fallbackString: 'Not Available',
        })}`,
      },
    ],
  },
});

const getRailAttributes = ({ departureData, arrivalData, isCopyCarrierAndRailcar }) => [
  {
    label: 'Date',
    value: getDateValue(departureData),
  },
  {
    label: 'Location',
    value: formatDataValue({
      value: departureData?.departureLocation,
      fallbackString: 'Not Available',
    }),
  },
  {
    label: 'Carrier',
    value: formatDataValue({
      value: departureData?.carrierName,
      fallbackString: 'Not Available',
    }),
  },
  {
    label: 'Railcar Number',
    value: formatDataValue({
      value: departureData?.railCarNumber,
      fallbackString: 'Not Available',
    }),
  },
  { label: 'Date', value: getDateValue(arrivalData) },
  {
    label: 'Location',
    value: formatDataValue({
      value: arrivalData?.arrivalLocation,
      fallbackString: 'Not Available',
    }),
  },
  {
    label: 'Carrier',
    value: formatDataValue({
      value: isCopyCarrierAndRailcar ? departureData?.carrierName : arrivalData?.carrierName,
      fallbackString: 'Not Available',
    }),
  },
  {
    label: 'Railcar Number',
    value: formatDataValue({
      value: isCopyCarrierAndRailcar ? departureData?.railCarNumber : arrivalData?.railCarNumber,
      fallbackString: 'Not Available',
    }),
  },
];

export const railMapper = ({
  departureData,
  arrivalData,
  skipDetailsInPrint,
  isCopyCarrierAndRailcar,
  isPrint,
  searchContextValues,
}) => {
  const railData = {
    title: arrivalData?.mode,
    titleChip: getTitleChip({ departureData, arrivalData, isPrint, searchContextValues }),
    header: [
      {
        label: railHeaderNameMapper(departureData?.name),
        value: getStatusChip({
          eventStatus: departureData?.status,
          isPrint,
          chipId: `${arrivalData?.mode}-dep`,
          searchContextValues,
        }),
        isComponent: true,
        isPending: isPending({ eventStatus: departureData?.status }),
      },
      {
        label: railHeaderNameMapper(arrivalData?.name),
        value: getStatusChip({
          eventStatus: arrivalData?.status,
          isPrint,
          chipId: `${arrivalData?.mode}-arr`,
          searchContextValues,
        }),
        isComponent: true,
        isPending: isPending({ eventStatus: arrivalData?.status }),
      },
    ],
    attributes: getRailAttributes({
      departureData,
      arrivalData,
      isCopyCarrierAndRailcar,
      skipDetailsInPrint,
    }),
  };
  if (!skipDetailsInPrint) {
    railData.details = {
      departure: [
        {
          label: 'Actual',
          value: getActualDate({ data: departureData }),
        },
        {
          label: 'Estimate',
          value: getEstimatedDate({ data: departureData }),
        },
      ],
      arrival: [
        {
          label: 'Actual',
          value: getActualDate({ data: arrivalData }),
        },
        {
          label: 'Estimate',
          value: getEstimatedDate({ data: arrivalData }),
        },
      ],
      details: [
        {
          label: 'Carrier',
          value: formatDataValue({
            value: departureData?.carrierName,
            fallbackString: 'Not Available',
          }),
        },
        {
          label: 'Railcar Number',
          value: formatDataValue({
            value: departureData?.railCarNumber,
            fallbackString: 'Not Available',
          }),
        },
        {
          label: 'Railcar Position',
          value: formatDataValue({
            value: departureData?.railCarPosition,
            fallbackString: 'Not Available',
          }),
        },
        {
          label: 'Railcar Equip. Type',
          value: formatDataValue({
            value: departureData?.railCarEquipmentType,
            fallbackString: 'Not Available',
          }),
        },
        {
          label: 'BOL Number',
          value: formatDataValue({
            value: departureData?.billOfLading,
            fallbackString: 'Not Available',
          }),
        },
        {
          label: 'Shipment ID',
          value: formatDataValue({
            value: departureData?.shipmentId,
            fallbackString: 'Not Available',
          }),
        },
        {
          label: 'Leg Origin - Leg Destination',
          value: `${formatDataValue({
            value: departureData?.departureLocation,
            fallbackString: 'Not Available',
          })} - ${formatDataValue({
            value: arrivalData?.arrivalLocation,
            fallbackString: 'Not Available',
          })}`,
        },
      ],
    };
  }
  return railData;
};

export const vesselMapper = ({
  departureData,
  arrivalData,
  vehicle,
  isExport,
  isFirstVesselLeg,
  isPrint,
  searchContextValues,
}) => ({
  title: 'Vessel',
  titleChip: null,
  header: [
    {
      label: 'Departure',
      value: getStatusChip({
        eventStatus: departureData?.status,
        isPrint,
        chipId: 'ves-dep',
        searchContextValues,
      }),
      isComponent: true,
      isPending: isPending({ eventStatus: departureData?.status }),
    },
    {
      label: 'Arrival',
      value: getStatusChip({
        eventStatus: arrivalData?.status,
        isPrint,
        chipId: 'ves-arr',
        searchContextValues,
      }),
      isComponent: true,
      isPending: isPending({ eventStatus: arrivalData?.status }),
    },
  ],
  attributes: [
    {
      label: 'Date',
      value: getDateValue(departureData),
    },
    {
      label: 'Location',
      value: formatDataValue({
        value: departureData?.departureLocation,
        fallbackString: 'Not Available',
      }),
    },
    {
      label: 'Vessel Name',
      value: formatDataValue({
        value: isFirstVesselLeg ? vehicle?.vehicleTracking?.vesselTransport?.vesselName : null,
        fallbackString: 'Not Available',
      }),
    },
    {
      label: 'Vessel',
      value: formatDataValue({
        value: getVesselValue({ vehicle }),
      }),
    },
    { label: 'Date', value: getDateValue(arrivalData) },
    {
      label: 'Location',
      value: formatDataValue({
        value: arrivalData?.arrivalLocation,
        fallbackString: 'Not Available',
      }),
    },
    {
      label: 'Vessel Name',
      value: formatDataValue({
        value: isFirstVesselLeg ? vehicle?.vehicleTracking?.vesselTransport?.vesselName : null,
        fallbackString: 'Not Available',
      }),
    },
    {
      label: 'Vessel',
      value: formatDataValue({
        value: getVesselValue({ vehicle }),
      }),
    },
  ],
  details: {
    departure: [
      {
        label: 'Actual',
        value: getActualDate({ data: departureData }),
      },
      {
        label: 'Estimate',
        value: getEstimatedDate({ data: departureData }),
      },
    ],
    arrival: [
      {
        label: 'Actual',
        value: getActualDate({ data: arrivalData }),
      },
      {
        label: 'Estimate',
        value: getEstimatedDate({ data: arrivalData }),
      },
    ],
    details: [
      {
        label: 'Shipper Name',
        value: formatDataValue({
          value: isFirstVesselLeg ? vehicle?.vehicleTracking?.vesselTransport?.shipperName : null,
          fallbackString: 'Not Available',
        }),
      },
      {
        label: 'Shipping Company',
        value: formatDataValue({
          value: isFirstVesselLeg
            ? vehicle?.vehicleTracking?.vesselTransport?.shippingCompanyName
            : null,
          fallbackString: 'Not Available',
        }),
      },
      {
        label: 'Shipping Priority',
        value: vehicle?.vehicleTracking?.vesselTransport?.shippingPriority ? (
          <CustomChip
            label={formatShippingPriority(
              vehicle?.vehicleTracking?.vesselTransport?.shippingPriority,
            )}
            color={chipColorMap?.qualityConfActiveText}
            backgroundColor={chipColorMap?.qualityConfActiveBackground}
            iconPosition="right"
            isPrint={isPrint}
            searchContextValues={searchContextValues}
          />
        ) : (
          'Not Available'
        ),
      },
      {
        label: 'TMC Vessel No.',
        value: formatDataValue({
          value: isFirstVesselLeg ? vehicle?.vehicleTracking?.vesselTransport?.vesselNumber : null,
          fallbackString: 'Not Available',
        }),
      },
      {
        label: 'Final Voyage No.',
        value: formatDataValue({
          value: isFirstVesselLeg
            ? vehicle?.vehicleTracking?.vesselTransport?.finalVoyageNumber
            : null,
          fallbackString: 'Not Available',
        }),
      },
      {
        label: 'Case No.',
        value: formatDataValue({
          value: isFirstVesselLeg ? vehicle?.vehicleTracking?.vesselTransport?.caseNumber : null,
          fallbackString: 'Not Available',
        }),
      },
    ],
    transportDetails: {
      title: 'Vessel',
      titleChip: null,
      header: [
        {
          label: 'Estimates',
          value: '',
        },
        {
          label: 'Actuals',
          value: '',
        },
      ],
      shipmentDetails: [
        {
          label: 'Shipper Name',
          value: formatDataValue({
            value: isFirstVesselLeg ? vehicle?.vehicleTracking?.vesselTransport?.shipperName : null,
            fallbackString: 'Not Available',
          }),
        },
        {
          label: 'Shipping Company',
          value: formatDataValue({
            value: isFirstVesselLeg
              ? vehicle?.vehicleTracking?.vesselTransport?.shippingCompanyName
              : null,
            fallbackString: 'Not Available',
          }),
        },
        {
          label: 'Shipping Priority',
          value: vehicle?.vehicleTracking?.vesselTransport?.shippingPriority ? (
            <CustomChip
              label={formatShippingPriority(
                vehicle?.vehicleTracking?.vesselTransport?.shippingPriority,
              )}
              color={chipColorMap?.qualityConfActiveText}
              backgroundColor={chipColorMap?.qualityConfActiveBackground}
              iconPosition="right"
              isPrint={isPrint}
              searchContextValues={searchContextValues}
            />
          ) : (
            'Not Available'
          ),
        },
        ...(isExport
          ? [
              {
                label: 'Loading Port',
                value: formatDataValue({
                  value: isFirstVesselLeg
                    ? vehicle?.vehicleTracking?.vesselTransport?.exportVehicleLoadingPort
                    : null,
                  fallbackString: 'Not Available',
                }),
              },
            ]
          : []),
        {
          label: 'TMC Vessel No.',
          value: formatDataValue({
            value: isFirstVesselLeg
              ? vehicle?.vehicleTracking?.vesselTransport?.vesselNumber
              : null,
            fallbackString: 'Not Available',
          }),
        },
        {
          label: 'Final Voyage No.',
          value: formatDataValue({
            value: isFirstVesselLeg
              ? vehicle?.vehicleTracking?.vesselTransport?.finalVoyageNumber
              : null,
            fallbackString: 'Not Available',
          }),
        },
        {
          label: 'Case No.',
          value: formatDataValue({
            value: isFirstVesselLeg ? vehicle?.vehicleTracking?.vesselTransport?.caseNumber : null,
            fallbackString: 'Not Available',
          }),
        },
        ...(isExport
          ? [
              {
                label: 'Discharge Port',
                value: formatDataValue({
                  value: isFirstVesselLeg
                    ? vehicle?.vehicleTracking?.vesselTransport?.exportVehicleDischargePort
                    : null,
                  fallbackString: 'Not Available',
                }),
              },
            ]
          : []),
      ],
      actualLoadDates: isExport
        ? [
            {
              label: 'Pre-Load Date',
              value: formatDataValue({
                value: isFirstVesselLeg
                  ? vehicle?.vehicleTracking?.vesselTransport?.preLoadDateFmt
                  : null,
                fallbackString: 'Not Available',
              }),
            },
            {
              label: 'Final Load Date',
              value: formatDataValue({
                value: isFirstVesselLeg
                  ? vehicle?.vehicleTracking?.vesselTransport?.finalLoadDateFmt
                  : null,
                fallbackString: 'Not Available',
              }),
            },
          ]
        : [],
    },
    vesselDetails: !isExport
      ? {
          title: 'Vessel',
          titleChip: null,
          header: [
            {
              label: 'Estimates',
              value: '',
            },
            {
              label: 'Actuals',
              value: '',
            },
          ],
          headerAttributes: [
            {
              label: 'TVA Date',
              value: formatDataValue({
                value: isFirstVesselLeg
                  ? vehicle?.vehicleTracking?.vesselTransport?.tvaDateFmt
                  : null,
                fallbackString: 'Not Available',
              }),
            },
          ],
          attributes: [
            {
              label: 'Final Inspection',
              value: formatDataValue({
                value: isFirstVesselLeg
                  ? vehicle?.manufacturing?.keyProductionDates?.finalInspectionDatePlannedFmt
                  : null,
                fallbackString: 'Not Available',
              }),
            },
            {
              label: 'Yard In Date',
              value: formatDataValue({
                value: isFirstVesselLeg
                  ? vehicle?.manufacturing?.keyProductionDates?.yardInDatePlannedFmt
                  : null,
                fallbackString: 'Not Available',
              }),
            },
            {
              label: 'Cargo Ready Date',
              value: formatDataValue({
                value: isFirstVesselLeg
                  ? vehicle?.vehicleTracking?.vesselTransport?.cargoReadyDatePlannedFmt
                  : null,
                fallbackString: 'Not Available',
              }),
            },
            {
              label: 'Gate Out Date',
              value: formatDataValue({
                value: isFirstVesselLeg
                  ? vehicle?.manufacturing?.keyProductionDates?.gateOutDatePlannedFmt
                  : null,
                fallbackString: 'Not Available',
              }),
            },
            {
              label: 'Final Inspection',
              value: formatDataValue({
                value: isFirstVesselLeg
                  ? vehicle?.manufacturing?.keyProductionDates?.finalInspectionDateActualFmt
                  : null,
                fallbackString: 'Not Available',
              }),
            },
            {
              label: 'Yard In Date',
              value: formatDataValue({
                value: isFirstVesselLeg
                  ? vehicle?.manufacturing?.keyProductionDates?.yardInDateActualFmt
                  : null,
                fallbackString: 'Not Available',
              }),
            },
            {
              label: 'Cargo Ready Date',
              value: formatDataValue({
                value: isFirstVesselLeg
                  ? vehicle?.vehicleTracking?.vesselTransport?.cargoReadyDateActualFmt
                  : null,
                fallbackString: 'Not Available',
              }),
            },
            {
              label: 'Gate Out Date',
              value: formatDataValue({
                value: isFirstVesselLeg
                  ? vehicle?.manufacturing?.keyProductionDates?.gateOutDateActualFmt
                  : null,
                fallbackString: 'Not Available',
              }),
            },
          ],
        }
      : {},
  },
});

const getPrintTransportationData = (events = []) => {
  const printEvents = [];
  events &&
    events?.forEach((event) => {
      if (!isEmpty(event?.details)) {
        printEvents?.push({ ...event, skipDetailsInPrint: true });
        event?.details?.forEach((detail) => {
          printEvents?.push(detail);
        });
      } else {
        printEvents?.push(event);
      }
    });
  return printEvents;
};
export const transportation = ({
  vehicle,
  vehicleType,
  transportation,
  isPrint,
  searchContextValues = {},
}) => {
  const transportationData = isPrint
    ? getPrintTransportationData(transportation?.Transportation?.events)
    : transportation?.Transportation?.events;

  const isExport = vehicleType === 'export';
  let isFirstVesselLeg = true;

  const sectionsData = [];
  forEach(transportationData, (item) => {
    if (item?.departure?.name === 'Truck Depart' && item?.departure?.type === 'Logistics') {
      sectionsData?.push(
        truckMapper({
          departureData: item?.departure,
          arrivalData: item?.arrival,
          vehicle,
          isPrint,
          searchContextValues,
        }),
      );
    }
    if (item?.departure?.name === 'Rail Depart' && item?.departure?.type === 'Logistics') {
      sectionsData?.push({
        ...railMapper({
          departureData: item?.departure,
          arrivalData: item?.arrival,
          skipDetailsInPrint: item?.skipDetailsInPrint,
          isCopyCarrierAndRailcar: isEmpty(item?.details),
          isPrint,
          searchContextValues,
        }),
        intersectionDetails: map(item?.details, (intersectionDetail) =>
          railMapper({
            departureData: intersectionDetail?.departure,
            arrivalData: intersectionDetail?.arrival,
            isPrint,
            searchContextValues,
          }),
        ),
      });
    }
    if (
      item?.departure?.name === 'On Truck for Delivery' &&
      item?.departure?.type === 'Logistics'
    ) {
      sectionsData?.push(
        deliveryMapper({
          departureData: item?.departure,
          arrivalData: item?.arrival,
          vehicle,
          isPrint,
          searchContextValues,
        }),
      );
    }
    if (
      item?.departure?.name === 'Rail Interchange Depart' &&
      item?.departure?.type === 'Logistics'
    ) {
      sectionsData?.push(
        railInterchangeMapper({
          departureData: item?.departure,
          arrivalData: item?.arrival,
          skipDetailsInPrint: item?.skipDetailsInPrint,
          isPrint,
          searchContextValues,
        }),
      );
    }
    if (item?.departure?.name === 'Vessel Depart' && item?.departure?.type === 'Logistics') {
      sectionsData?.push(
        vesselMapper({
          departureData: item?.departure,
          arrivalData: item?.arrival,
          vehicle,
          isExport,
          isFirstVesselLeg,
          isPrint,
          searchContextValues,
        }),
      );
      isFirstVesselLeg = false;
    }
  });

  return {
    sectionsData,
    hero: [
      {
        label: 'Return to Vehicle Journey',
        value: <LinkToSectionScroll label="Vehicle Journey" scrollToId="vehicle_journey" />,
      },
      ...getSharedLabelsAndValues(
        ['lifecyclePhase', 'categoryCode', 'categoryDescription'],
        vehicle,
        {
          isPrint,
          searchContextValues,
        },
      ),
    ],
  };
};
